import { TextField, type TextFieldProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

const authTextFieldSx: SxProps<Theme> = {
  "& .MuiInputLabel-root": {
    color: "var(--muted-foreground)",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    transform: "translate(14px, 14px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
    "&.Mui-focused": {
      color: "var(--foreground)",
    },
    "&.Mui-error": {
      color: "var(--destructive)",
    },
  },
  "& .MuiOutlinedInput-root": {
    minHeight: 48,
    borderRadius: "var(--radius-lg)",
    backgroundColor: "transparent",
    fontSize: "0.875rem",
    transition: "border-color 150ms ease, box-shadow 150ms ease",
    "& fieldset": {
      borderColor: "var(--input)",
    },
    "&:hover fieldset": {
      borderColor: "var(--input)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--ring)",
      borderWidth: 1,
    },
    "&.Mui-error fieldset": {
      borderColor: "var(--destructive)",
    },
    "&.Mui-disabled": {
      backgroundColor: "color-mix(in oklab, var(--input) 50%, transparent)",
      opacity: 0.5,
    },
  },
  "& .MuiInputBase-input": {
    boxSizing: "border-box",
    height: 48,
    padding: "0 10px",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    color: "var(--foreground)",
    "&::placeholder": {
      color: "var(--muted-foreground)",
      opacity: 1,
    },
  },
  "& .MuiInputBase-inputAdornedEnd": {
    paddingRight: 0,
  },
  "& .MuiFormHelperText-root": {
    marginInline: "12px",
    marginTop: "4px",
    fontSize: "0.75rem",
    lineHeight: "1rem",
    "&.Mui-error": {
      color: "var(--destructive)",
    },
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "48px !important",
    minHeight: "0 !important",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "10px !important",
  },
  "& .MuiSvgIcon-root": {
    color: "var(--muted-foreground)",
  },
};

const authSelectMenuPaperSx: SxProps<Theme> = {
  mt: 0.5,
  p: 0.5,
  borderRadius: "var(--radius-lg)",
  border: "1px solid var(--border)",
  boxShadow: "0 8px 24px rgb(0 0 0 / 8%)",
  "& .MuiMenu-list": {
    p: 0,
  },
  "& .MuiMenuItem-root": {
    mx: 0,
    my: 0.25,
    minHeight: 40,
    borderRadius: "var(--radius-md)",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: "color-mix(in oklab, var(--primary) 10%, transparent)",
    },
    "&.Mui-selected": {
      backgroundColor: "transparent",
      color: "var(--primary)",
    },
    "&.Mui-selected.Mui-focusVisible": {
      backgroundColor: "transparent",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "color-mix(in oklab, var(--primary) 16%, transparent)",
    },
  },
};

const AuthTextField = ({ SelectProps, sx, ...props }: TextFieldProps) => {
  const selectMenuPaperSx = SelectProps?.MenuProps?.PaperProps?.sx;

  return (
    <TextField
      sx={[authTextFieldSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      SelectProps={{
        ...SelectProps,
        MenuProps: {
          ...SelectProps?.MenuProps,
          PaperProps: {
            ...SelectProps?.MenuProps?.PaperProps,
            elevation: 0,
            sx: [
              authSelectMenuPaperSx,
              ...(Array.isArray(selectMenuPaperSx)
                ? selectMenuPaperSx
                : selectMenuPaperSx
                  ? [selectMenuPaperSx]
                  : []),
            ],
          },
        },
      }}
      {...props}
    />
  );
};

export default AuthTextField;
